import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Package, Search, ChevronDown } from 'lucide-react';
import { useOrders } from '../context/OrdersContext';
import { useProducts } from '../context/ProductsContext';
import { usePaymentTypes } from '../context/PaymentTypeContext';

/* ─── Field wrapper ─────────────────────────────────────── */
const Field = ({ label, required, helper, counter, maxLen, valueLen, children, error }) => (
  <div className="mb-5">
    <div className="flex items-center gap-2 mb-2">
      {required && <span className="w-2.5 h-2.5 rounded-full bg-orange-500 shrink-0" />}
      <label className="text-sm font-semibold text-gray-700">
        {label}{required && ' *'}
      </label>
    </div>
    {children}
    <div className="flex justify-between mt-1 px-0.5">
      {error
        ? <p className="text-xs text-red-500">{error}</p>
        : helper
          ? <p className="text-xs text-gray-400">{helper}</p>
          : <span />}
      {counter && <p className="text-xs text-gray-400">{valueLen ?? 0}/{maxLen}</p>}
    </div>
  </div>
);

const inp =
  'w-full px-4 py-2.5 rounded-lg border border-[#e0d5c5] bg-[#faf6f0] text-sm ' +
  'text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 ' +
  'focus:ring-teal-500 focus:border-transparent transition';

/* ─── Searchable Product Select ─────────────────────────── */
const SearchableProductSelect = ({ products, value, onChange, loading, error }) => {
  const [open, setOpen]         = useState(false);
  const [query, setQuery]       = useState('');
  const containerRef            = useRef(null);
  const searchRef               = useRef(null);

  const selected = value ? products.find(p => String(p.id) === String(value)) : null;

  const filtered = query.trim()
    ? products.filter(p =>
        p.name.toLowerCase().includes(query.toLowerCase()) ||
        String(p.price ?? p.sale_price).includes(query)
      )
    : products;

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [open]);

  const handleSelect = (prod) => {
    onChange(prod);
    setOpen(false);
    setQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setQuery('');
  };

  if (loading) {
    return (
      <div className={inp + ' flex items-center gap-2 text-gray-400'}>
        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"
            strokeDasharray="60" strokeDashoffset="20" />
        </svg>
        Loading products…
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={
          'w-full px-4 py-2.5 rounded-lg border text-sm text-left flex items-center justify-between gap-2 transition ' +
          'focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ' +
          (error
            ? 'border-red-400 bg-red-50'
            : open
              ? 'border-teal-400 bg-[#faf6f0] ring-2 ring-teal-500'
              : 'border-[#e0d5c5] bg-[#faf6f0] hover:border-teal-300')
        }
      >
        <span className={selected ? 'text-gray-800 truncate' : 'text-gray-400'}>
          {selected
            ? truncate(selected.name, 42) + `  ·  ₹${selected.price ?? selected.sale_price}`
            : 'Select product…'}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {selected && (
            <span
              onClick={handleClear}
              className="w-5 h-5 rounded-full bg-gray-200 hover:bg-red-100 hover:text-red-500
                         flex items-center justify-center text-gray-400 transition cursor-pointer"
              title="Clear"
            >
              <X className="w-3 h-3" />
            </span>
          )}
          <ChevronDown
            className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        </span>
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 w-full rounded-xl border border-[#e0d5c5] bg-white shadow-xl overflow-hidden"
          style={{ maxHeight: '320px', display: 'flex', flexDirection: 'column' }}
        >
          <div className="px-3 py-2 border-b border-[#f0e8db] bg-[#fdf8f2] shrink-0">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-[#e0d5c5]">
              <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search products…"
                className="flex-1 text-sm text-gray-700 bg-transparent outline-none placeholder-gray-400"
              />
              {query && (
                <button onClick={() => setQuery('')} className="text-gray-400 hover:text-gray-600 transition">
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-400">
                No products match "{query}"
              </div>
            ) : (
              filtered.map(p => {
                const isSelected = String(p.id) === String(value);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => handleSelect(p)}
                    className={
                      'w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors ' +
                      (isSelected
                        ? 'bg-teal-50 border-l-2 border-teal-500'
                        : 'hover:bg-[#fdf8f2] border-l-2 border-transparent')
                    }
                  >
                    {p.images?.[0] ? (
                      <img src={p.images[0]} alt={p.name}
                        className="w-9 h-9 rounded-md object-cover border border-[#e8dfd0] shrink-0" />
                    ) : (
                      <div className="w-9 h-9 rounded-md bg-gray-100 border border-[#e8dfd0] flex items-center justify-center shrink-0">
                        <Package className="w-4 h-4 text-gray-300" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 font-medium truncate" title={p.name}>
                        {highlightMatch(p.name, query)}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs font-semibold text-teal-600">₹{p.price ?? p.sale_price}</span>
                        {p.sku && <span className="text-xs text-gray-400 truncate">SKU: {p.sku}</span>}
                      </div>
                    </div>
                    {isSelected && <span className="text-teal-500 shrink-0 text-xs">✓</span>}
                  </button>
                );
              })
            )}
          </div>

          <div className="px-4 py-1.5 border-t border-[#f0e8db] bg-[#fdf8f2] shrink-0">
            <p className="text-xs text-gray-400">{filtered.length} of {products.length} products</p>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Helpers ───────────────────────────────────────────── */
const truncate = (str, max) => str.length <= max ? str : str.slice(0, max) + '…';

const highlightMatch = (text, query) => {
  if (!query.trim()) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-100 text-yellow-800 rounded px-0.5">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
};

/* ─── Initial form state ────────────────────────────────── */
const INITIAL = {
  customerName: '',
  customerEmail: '',
  paymentMethod: '',
  paymentStatus: '',
  orderType: '',
  productId: '',
  itemDescription: '',
  quantity: '',
  unitPrice: '',
  shippingCharges: '50',
  tax: '18',
  notes: '',
};

/* ─── Main Component ────────────────────────────────────── */
const AddOrderModal = ({ open, onClose }) => {
  const { createOrder, loading: orderLoading } = useOrders();
  const { products, loading: productsLoading } = useProducts();
  const { paymentTypes, paymentTypesLoading }  = usePaymentTypes();

  const [form,     setForm]     = useState(INITIAL);
  const [errors,   setErrors]   = useState({});
  const [show,     setShow]     = useState(false);
  const [apiError, setApiError] = useState('');
  const [success,  setSuccess]  = useState(false);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => requestAnimationFrame(() => setShow(true)));
      setForm(INITIAL);
      setErrors({});
      setApiError('');
      setSuccess(false);
    } else {
      setShow(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [open, onClose]);

  if (!open) return null;

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const qty      = parseInt(form.quantity, 10);
  const price    = parseFloat(form.unitPrice);
  // const shipping = parseFloat(form.shippingCharges) || 0;
  const tax      = parseFloat(form.tax) || 0;
  const subtotal = !isNaN(price) && !isNaN(qty) ? price * qty : null;
  const total    = subtotal !== null ? (subtotal + tax).toFixed(2) : null;

  const validate = () => {
    const e = {};
    if (!form.customerName.trim())                        e.customerName    = 'Customer name is required';
    if (!form.customerEmail.trim())                       e.customerEmail   = 'Email is required';
    else if (!/^\S+@\S+\.\S+$/.test(form.customerEmail)) e.customerEmail   = 'Invalid email';
    if (!form.paymentMethod)                              e.paymentMethod   = 'Required';
    if (!form.paymentStatus)                              e.paymentStatus   = 'Required';
    if (!form.orderType)                                  e.orderType       = 'Required';
    if (!form.productId)                                  e.productId       = 'Please select a product';
    if (!form.itemDescription.trim())                     e.itemDescription = 'Required';
    if (!form.quantity || qty < 1)                        e.quantity        = 'Enter valid qty';
    if (!form.unitPrice || isNaN(price) || price < 0)    e.unitPrice       = 'Enter valid price';
    setErrors(e);
    return !Object.keys(e).length;
  };
const buildPayload = () => ({
  
  user_name:    form.customerName.trim(),
  user_email:   form.customerEmail.trim(),
  order_type:       form.orderType,
  order_status:     'Placed',
  subtotal:         subtotal,
  shipping_charges: parseFloat(form.shippingCharges) || 50,
  tax:              tax,
  products: [
    {
      product_id: parseInt(form.productId, 10),
      quantity:   qty,
      price:      price,
    },
  ],
  payment_method:  form.paymentMethod,
  payment_status:  form.paymentStatus,
  ...(form.notes && { notes: form.notes.trim() }),
});

 const handleSubmit = async () => {
  setApiError('');
  if (!validate()) return;
  const payload = buildPayload();
  console.log('Sending payload:', JSON.stringify(payload, null, 2)); // ADD THIS
  const result = await createOrder(payload);

    if (result) {
      setSuccess(true);
      setTimeout(() => { setSuccess(false); onClose(); }, 1200);
    } else {
      setApiError('Failed to create order. Please try again.');
    }
  };

  const handleProductChange = (prod) => {
    if (!prod) {
      setForm(prev => ({ ...prev, productId: '', itemDescription: '', unitPrice: '' }));
      return;
    }
    setForm(prev => ({
      ...prev,
      productId:       String(prod.id),
      itemDescription: prod.name,
      unitPrice:       String(prod.price ?? prod.sale_price ?? ''),
    }));
  };

  const selectedProduct = form.productId
    ? products.find(p => String(p.id) === String(form.productId))
    : null;

  const loading = orderLoading;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40 transition-opacity duration-300"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className="fixed inset-0 z-50 flex"
        style={{
          backgroundColor: show ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0)',
          transition: 'background-color 300ms',
        }}
        onClick={onClose}
      >
        <div
          className="ml-auto h-full flex flex-col shadow-2xl relative"
          style={{
            width: 'min(520px, 100vw)',
            backgroundColor: '#fdf8f2',
            transform: show ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 300ms cubic-bezier(.4,0,.2,1)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-gray-900 text-white
                       flex items-center justify-center hover:bg-gray-700 transition-colors z-10"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>

          <div
            className="shrink-0 flex flex-col items-center pt-7 pb-5 border-b"
            style={{ borderColor: '#e8dfd0' }}
          >
            <h2 className="text-2xl font-bold text-teal-800 tracking-tight">Add New Order</h2>
            <div className="mt-2 w-16 h-1 rounded-full bg-orange-400" />
          </div>

          {apiError && (
            <div className="mx-4 mt-3 px-4 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600">
              {apiError}
            </div>
          )}

          {success && (
            <div className="mx-4 mt-3 px-4 py-2 rounded-lg bg-teal-50 border border-teal-200 text-sm text-teal-700 font-medium text-center">
              ✅ Order created successfully!
            </div>
          )}

          <div className="flex-1 overflow-y-auto pt-2 pb-2 bg-[#fee4c5]">
            <div className="p-8 space-y-8 min-h-[700px] rounded-[25px] m-4 bg-gray-400 bg-opacity-20">

              <p className="text-xs font-bold uppercase tracking-widest text-orange-500 mb-4">
                Customer Details
              </p>

              <Field label="Customer Name" required error={errors.customerName}>
                <input
                  type="text" className={inp}
                  placeholder="Enter customer name"
                  value={form.customerName}
                  onChange={set('customerName')}
                />
              </Field>

              <Field label="Customer Email" required error={errors.customerEmail}>
                <input
                  type="email" className={inp}
                  placeholder="Enter email address"
                  value={form.customerEmail}
                  onChange={set('customerEmail')}
                />
              </Field>

              <p className="text-xs font-bold uppercase tracking-widest text-orange-500 mb-4">
                Order Details
              </p>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Payment Method" required helper="How the customer will pay" error={errors.paymentMethod}>
                  <div className="relative">
                    <select
                      className={inp + ' appearance-none cursor-pointer pr-8'}
                      value={form.paymentMethod}
                      onChange={set('paymentMethod')}
                    >
                      <option value="">Select method…</option>
                      {['Upi', 'Credit Card', 'Debit Card', 'Net Banking', 'Cod', 'Wallet'].map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▾</span>
                  </div>
                </Field>

                <Field label="Order Type" required error={errors.orderType}>
                  <div className="relative">
                    {paymentTypesLoading ? (
                      <div className={inp + ' text-gray-400'}>Loading order types...</div>
                    ) : (
                      <>
                        <select
                          className={inp + ' appearance-none cursor-pointer pr-8'}
                          value={form.orderType}
                          onChange={set('orderType')}
                        >
                          <option value="">Select order type…</option>
                          {paymentTypes
                            .filter(type => type.status === 'Active')
                            .map((type) => (
                              <option key={type.id} value={type.type}>{type.type}</option>
                            ))}
                        </select>
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▾</span>
                      </>
                    )}
                  </div>
                </Field>

                {/* <Field label="Shipping Charges (₹)" helper="Default ₹50">
                  <input
                    type="number" min={0} step={1}
                    className={inp} placeholder="50"
                    value={form.shippingCharges}
                    onChange={set('shippingCharges')}
                  />
                </Field> */}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Tax (₹)" helper="Default ₹18">
                  <input
                    type="number" min={0} step={0.01}
                    className={inp} placeholder="18"
                    value={form.tax}
                    onChange={set('tax')}
                  />
                </Field>
                <div />
              </div>

              <Field label="Payment Status" required error={errors.paymentStatus}>
                <div className="flex gap-3 flex-wrap">
                  {['Pending', 'Paid'].map(s => (
                    <label key={s} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer text-sm transition-all ${
                      form.paymentStatus === s
                        ? 'bg-teal-50 border-teal-400 text-teal-700 font-medium'
                        : 'border-[#e0d5c5] bg-[#faf6f0] text-gray-600 hover:border-teal-300'
                    }`}>
                      <input
                        type="radio" name="paymentStatus" value={s}
                        checked={form.paymentStatus === s} onChange={set('paymentStatus')}
                        className="accent-teal-600"
                      />
                      {s}
                    </label>
                  ))}
                </div>
              </Field>

              <p className="text-xs font-bold uppercase tracking-widest text-orange-500 mb-4 mt-1">
                Item Details
              </p>

              <Field label="Select Product" required error={errors.productId}>
                <SearchableProductSelect
                  products={products}
                  value={form.productId}
                  onChange={handleProductChange}
                  loading={productsLoading}
                  error={errors.productId}
                />
                {selectedProduct && (
                  <div className="flex items-center gap-3 mt-2 px-3 py-2 rounded-lg bg-white border border-[#e0d5c5]">
                    {selectedProduct.images?.[0] ? (
                      <img src={selectedProduct.images[0]} alt={selectedProduct.name}
                        className="w-12 h-12 rounded-lg object-cover border" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gray-100 border flex items-center justify-center">
                        <Package className="w-5 h-5 text-gray-400" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-700 truncate">{selectedProduct.name}</p>
                      <p className="text-xs text-teal-600 font-semibold">
                        ₹{selectedProduct.price ?? selectedProduct.sale_price}
                      </p>
                      {selectedProduct.sku && (
                        <p className="text-xs text-gray-400">SKU: {selectedProduct.sku}</p>
                      )}
                    </div>
                  </div>
                )}
              </Field>

              <Field
                label="Item Description" required
                helper="Describe what is being ordered"
                counter maxLen={100} valueLen={form.itemDescription.length}
                error={errors.itemDescription}
              >
                <input
                  type="text" className={inp}
                  placeholder="e.g. Custom Business Cards (250 pcs)"
                  maxLength={100}
                  value={form.itemDescription}
                  onChange={set('itemDescription')}
                />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Quantity" required helper="Number of units" error={errors.quantity}>
                  <input
                    type="number" min={1}
                    className={inp} placeholder="e.g. 5"
                    value={form.quantity}
                    onChange={set('quantity')}
                  />
                </Field>
                <Field label="Unit Price (₹)" required helper="Price per unit" error={errors.unitPrice}>
                  <input
                    type="number" min={0} step={0.01}
                    className={inp} placeholder="e.g. 499.00"
                    value={form.unitPrice}
                    onChange={set('unitPrice')}
                  />
                </Field>
              </div>

              {subtotal !== null && (
                <div className="mb-5 rounded-xl bg-teal-50 border border-teal-200 overflow-hidden">
                  <div className="px-4 py-2 flex items-center justify-between border-b border-teal-100">
                    <span className="text-sm text-teal-600">Subtotal</span>
                    <span className="text-sm font-medium text-teal-700">₹{subtotal.toFixed(2)}</span>
                  </div>
                  {/* <div className="px-4 py-2 flex items-center justify-between border-b border-teal-100">
                    <span className="text-sm text-teal-600">Shipping</span>
                    <span className="text-sm font-medium text-teal-700">₹{shipping.toFixed(2)}</span>
                  </div> */}
                  <div className="px-4 py-2 flex items-center justify-between border-b border-teal-100">
                    <span className="text-sm text-teal-600">Tax</span>
                    <span className="text-sm font-medium text-teal-700">₹{tax.toFixed(2)}</span>
                  </div>
                  <div className="px-4 py-3 flex items-center justify-between bg-teal-100">
                    <span className="text-sm text-teal-700 font-semibold">Total</span>
                    <span className="text-lg font-bold text-teal-800">₹{total}</span>
                  </div>
                </div>
              )}

              <Field
                label="Notes" helper="Special instructions or remarks"
                counter maxLen={200} valueLen={form.notes.length}
              >
                <textarea
                  rows={3} className={inp + ' resize-none'}
                  placeholder="e.g. Please deliver before 5 PM…"
                  maxLength={200}
                  value={form.notes}
                  onChange={set('notes')}
                />
              </Field>

              <div className="flex w-full gap-3 pt-2">
                <button
                  onClick={onClose}
                  disabled={loading}
                  className="flex-1 px-5 py-2.5 rounded-lg border border-[#d0c0a8] text-sm font-medium
                             text-gray-600 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading || success}
                  className="flex-1 px-6 py-2.5 rounded-lg bg-teal-700 text-white text-sm font-semibold
                             hover:bg-teal-800 transition-colors shadow-sm disabled:opacity-60
                             flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"
                          strokeDasharray="60" strokeDashoffset="20" />
                      </svg>
                      Creating…
                    </>
                  ) : 'Create Order'}
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddOrderModal;